// src/modulos/Parametros/Tipos/TypeProjectPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useSubCollection from '../../../hooks/useSubCollection';
import usePagePermissions from '../../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../../components/common';

// Import utilities
import { getTimestamps } from '../../../utils/timestamps';

// Page-specific styles
import styles from '../Parameters.module.css';

// Constantes
const PROJECT_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-projeto'
};
const ITEMS_PER_PAGE = 10;

const TypeProjectPage = () => {
    const can = usePagePermissions('tipo_de_projeto');

    // Estados Locais
    const [editingProject, setEditingProject] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hook de Dados
    const {
        data: projects,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useSubCollection(
        PROJECT_COLLECTION_PATH.parentCollection,
        PROJECT_COLLECTION_PATH.parentId,
        PROJECT_COLLECTION_PATH.subCollectionName,
        'name'
    );

    // ============================================================
    // 1. DEFINIR filteredProjects ANTES dos callbacks
    // ============================================================
    const filteredProjects = useMemo(() => {
        if (!projects) return [];
        if (!searchTerm) return projects;

        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return projects.filter(item =>
            item.name.toLowerCase().includes(lowerCaseSearchTerm)
        );
    }, [projects, searchTerm]);

    const totalFilteredItems = filteredProjects.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const paginatedProjects = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredProjects.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredProjects, currentPage]);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingProject(null);
        setInputValue('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingProject(item);
        setInputValue(item.name);
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Tipo de Projeto "${name}" deletado com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const newTotalItems = totalFilteredItems - 1;
                const maxPage = Math.ceil(newTotalItems / ITEMS_PER_PAGE);
                if (currentPage > maxPage && maxPage > 0) {
                    setCurrentPage(maxPage);
                } else if (newTotalItems === 0) {
                    setCurrentPage(1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar Tipo de Projeto: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar tipo de projeto. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, deleteDocument, totalFilteredItems, currentPage]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!inputValue.trim()) {
            setFormError("O nome do Tipo de Projeto é obrigatório.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingProject, userEmail);
        const data = {
            name: inputValue.trim(),
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingProject) {
                result = await updateDocument(editingProject.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingProject ? 'Tipo de Projeto editado com sucesso!' : 'Tipo de Projeto adicionado com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingProject ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [inputValue, editingProject, addDocument, updateDocument, handleCloseModal]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingProject ? `Editar: ${editingProject.name}` : 'Adicionar Novo Tipo de Projeto'}
                subtitle={editingProject ? `Editando: ${editingProject.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingProject ? '💾 Salvar Edição' : '✨ Adicionar Tipo'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Nome do Tipo de Projeto <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        className={styles.input}
                        placeholder="Ex: Comercial, Interno, Consultoria"
                        required
                        disabled={formLoading}
                        autoFocus
                    />
                    <small className={styles.fieldHint}>
                        Nome que identifica a categoria do projeto
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar o Tipo de Projeto "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Tipos de Projeto"
                actionText="Adicionar Tipo"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar tipo de projeto..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={2} rows={ITEMS_PER_PAGE} />
                    ) : paginatedProjects.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📊'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhum tipo de projeto cadastrado'
                            }
                            message={
                                searchTerm
                                    ? `Não encontramos resultados para "${searchTerm}"`
                                    : "Clique em '+ Adicionar Tipo' para começar"
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Nome do Tipo de Projeto</th>
                                            <th style={{ width: '150px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedProjects.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Atualizado: {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="tipos"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default TypeProjectPage;